import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { SERVER_API_URL, SERVER_API_THAM_DINH } from '../constants/app.constants';
import { ThongBaoDemChuaDoc, ThongBaoItem, ThongBaoPage } from '../models/thong-bao.model';

/** Chu kỳ poll số chưa đọc. Backend chưa push inbox qua WebSocket nên tạm poll. */
const CHU_KY_POLL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class ThongBaoService implements OnDestroy {
  private http = inject(HttpClient);
  private zone = inject(NgZone);

  private readonly baseUrl = `${SERVER_API_URL}${SERVER_API_THAM_DINH}/kinh-doanh`;

  private soChuaDocSubject = new BehaviorSubject<number>(0);
  public readonly soChuaDoc$ = this.soChuaDocSubject.asObservable();

  private pollSub?: Subscription;

  ngOnDestroy(): void {
    this.dungTheoDoi();
  }

  /** Danh sách thông báo mới → cũ (backend sắp cố định theo timestamp desc), page bắt đầu từ 0. */
  layDanhSach(page: number, size = 20): Observable<ThongBaoPage> {
    const params = new HttpParams().set('amount', size).set('page', page);
    return this.http
      .get<any>(`${this.baseUrl}/list-thong-bao-app-kinh-doanh`, { params })
      .pipe(map(res => this.layPayload<ThongBaoPage>(res, 'Không tải được thông báo')));
  }

  /** Gọi không kèm `amount` thì backend trả về bộ đếm chưa đọc thay vì danh sách. */
  demChuaDoc(): Observable<number> {
    return this.http
      .get<any>(`${this.baseUrl}/list-thong-bao-app-kinh-doanh`)
      .pipe(
        map(res => this.layPayload<ThongBaoDemChuaDoc>(res, 'Không tải được số thông báo')),
        map(dem => Number(dem?.number_of_messages) || 0)
      );
  }

  danhDauDaDoc(id: string): Observable<void> {
    return this.guiDanhDau(false, id).pipe(
      tap(() => this.soChuaDocSubject.next(Math.max(0, this.soChuaDocSubject.value - 1)))
    );
  }

  danhDauTatCaDaDoc(): Observable<void> {
    // @RequestBody String ở backend là bắt buộc nên vẫn phải gửi một chuỗi dù readAll=true.
    return this.guiDanhDau(true, 'all').pipe(tap(() => this.soChuaDocSubject.next(0)));
  }

  /** Tải lại số chưa đọc ngay; lỗi thì giữ số cũ để không làm vỡ navbar. */
  capNhatSoChuaDoc(): void {
    this.demChuaDoc()
      .pipe(catchError(() => of(null)))
      .subscribe(so => {
        if (so !== null) this.soChuaDocSubject.next(so);
      });
  }

  /**
   * Đếm ngay khi gọi (badge có số trước khi người dùng mở dropdown), sau đó poll định kỳ và đếm lại
   * khi tab được focus trở lại. Gọi nhiều lần không tạo thêm luồng poll.
   */
  batDauTheoDoi(): void {
    if (this.pollSub) return;
    const khiQuayLaiTab$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible')
    );
    // Chạy timer ngoài Angular zone để poll không kích hoạt change detection mỗi chu kỳ khi rảnh.
    this.zone.runOutsideAngular(() => {
      this.pollSub = merge(timer(0, CHU_KY_POLL_MS), khiQuayLaiTab$)
        .pipe(
          filter(() => document.visibilityState !== 'hidden'),
          switchMap(() => this.demChuaDoc().pipe(catchError(() => of(null))))
        )
        .subscribe(so => {
          if (so !== null) this.zone.run(() => this.soChuaDocSubject.next(so));
        });
    });
  }

  dungTheoDoi(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.soChuaDocSubject.next(0);
  }

  laDaDoc(item: ThongBaoItem): boolean {
    return !!(item.read ?? item.isRead);
  }

  private guiDanhDau(readAll: boolean, body: string): Observable<void> {
    const params = new HttpParams().set('readAll', readAll);
    const headers = new HttpHeaders({ 'Content-Type': 'text/plain' });
    return this.http
      .post<any>(`${this.baseUrl}/doc-thong-bao-app-kinh-doanh`, body, { params, headers })
      .pipe(map(res => {
        this.layPayload<unknown>(res, 'Không cập nhật được trạng thái thông báo');
      }));
  }

  /**
   * Envelope backend: lỗi nghiệp vụ vẫn HTTP 200 với status = 1.
   * `ResponseDTO.setPayLoadContent(x)` bọc thêm một lớp `{ data: x }` trong `payload`, nên phải bóc
   * `payload.data` mới ra được Page / bộ đếm chưa đọc.
   */
  private layPayload<T>(res: any, thongBaoLoi: string): T {
    if (res && res.status !== undefined && res.status !== 0) {
      throw new Error(res.description || thongBaoLoi);
    }
    const payload = res?.payLoadContent ?? res?.payload;
    return (payload && payload.data !== undefined ? payload.data : payload) as T;
  }
}
